import React, { useEffect, useState, useContext } from "react";
import styles from "./Planner.module.css";
import { UserContext } from "../../context/UserContext";
import { db } from "../../services/firebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";

/**
 * PlannerPage - ajustado:
 * - removei o campo de busca da página (fica apenas em Library)
 * - removi painel de sugestões lateral; grade semanal ocupa espaço livre
 * - salva em users/{uid}.plans (merge)
 */

const weekdays = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const axes = ["mind", "move", "nutri", "values"];

export default function PlannerPage() {
  const { user, userData } = useContext(UserContext);
  const uid = user?.uid;

  const [activitiesLists, setActivitiesLists] = useState({
    mind: [],
    move: [],
    nutri: [],
    values: [],
  });
  const [localPlans, setLocalPlans] = useState(userData?.plans || {});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAxis, setSelectedAxis] = useState("mind");
  const [selectedDay, setSelectedDay] = useState("monday");

  useEffect(() => {
    setLocalPlans(userData?.plans || {});
  }, [userData]);

  useEffect(() => {
    if (!uid) return;
    let mounted = true;
    async function loadLists() {
      try {
        setLoading(true);
        const docNames = [
          { key: "mind", docId: "mindActsList", arrayKey: "mindActsList" },
          { key: "move", docId: "moveActsList", arrayKey: "moveActsList" },
          { key: "nutri", docId: "nutriActsList", arrayKey: "nutriActsList" },
          {
            key: "values",
            docId: "valuesActsList",
            arrayKey: "valuesActsList",
          },
        ];
        const lists = { mind: [], move: [], nutri: [], values: [] };
        await Promise.all(
          docNames.map(async (d) => {
            const snap = await getDoc(doc(db, "activities", d.docId));
            if (snap.exists()) {
              const data = snap.data();
              const arr = data?.[d.arrayKey] || [];
              lists[d.key] = Array.isArray(arr) ? arr.slice(0, 200) : [];
            }
          })
        );
        if (mounted) setActivitiesLists(lists);
      } catch (err) {
        console.error("Erro load activities", err);
        if (mounted) setError("Erro ao carregar atividades");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadLists();
    return () => {
      mounted = false;
    };
  }, [uid]);

  function getDayList(axis, day) {
    return (localPlans?.[axis]?.[day] || []).slice();
  }

  function addToDay(axis, day, activity) {
    const plansCopy = JSON.parse(JSON.stringify(localPlans || {}));
    if (!plansCopy[axis])
      plansCopy[axis] = {
        sunday: [],
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        repeatWeekly: true,
      };
    if (!Array.isArray(plansCopy[axis][day])) plansCopy[axis][day] = [];
    const item = {
      id: activity.id || activity.act || Math.random().toString(36).slice(2, 9),
      title: activity.title || activity.content || activity.act || "Atividade",
      axis,
      dateAddedAt: new Date().toISOString(),
      userCreated: false,
      url: activity.url || null,
      value: activity.value,
    };
    plansCopy[axis][day].push(item);
    setLocalPlans(plansCopy);
  }

  function removeFromDay(axis, day, idx) {
    const plansCopy = JSON.parse(JSON.stringify(localPlans || {}));
    if (!plansCopy[axis] || !Array.isArray(plansCopy[axis][day])) return;
    plansCopy[axis][day].splice(idx, 1);
    setLocalPlans(plansCopy);
  }

  async function savePlans() {
    if (!uid) return;
    setSaving(true);
    setError(null);
    try {
      await setDoc(
        doc(db, "users", uid),
        { plans: localPlans, plansUpdatedAt: new Date() },
        { merge: true }
      );
    } catch (err) {
      console.error("Erro ao salvar plano", err);
      setError("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  }

  // suggestions list (for quick adds) — limited and axis-filtered to help on wide screens
  const suggestions = (activitiesLists[selectedAxis] || []).slice(0, 6);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Planner semanal</h1>
        <p>Monte sua semana — adicione com um clique e salve.</p>
      </header>

      <main className={styles.main}>
        <aside className={styles.controls}>
          <div className={styles.field}>
            <label>Axis</label>
            <select
              value={selectedAxis}
              onChange={(e) => setSelectedAxis(e.target.value)}
            >
              {axes.map((ax) => (
                <option value={ax} key={ax}>
                  {ax.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label>Dia</label>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
            >
              {weekdays.map((d) => (
                <option value={d} key={d}>
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <button
              className="saveButton"
              onClick={savePlans}
              disabled={saving}
            >
              {saving ? "Salvando..." : "Salvar plano"}
            </button>
          </div>

          <div className={styles.field}>
            <h4>Sugestões rápidas ({selectedAxis.toUpperCase()})</h4>
            {loading ? (
              <p>Carregando...</p>
            ) : suggestions.length === 0 ? (
              <p className={styles.empty}>Nenhuma sugestão</p>
            ) : (
              <ul className={styles.suggestionsList}>
                {suggestions.map((s, i) => (
                  <li key={s.id || i} className={styles.suggestionItem}>
                    <div>
                      <div className={styles.suggTitle}>
                        {s.title || s.content}
                      </div>
                      <div className={styles.suggMeta}>
                        {s.axis || selectedAxis}
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() => addToDay(selectedAxis, selectedDay, s)}
                        className="defaultButton"
                      >
                        Adicionar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        <section className={styles.weekGrid}>
          <h3>Semana (visão geral)</h3>
          <div className={styles.grid}>
            {weekdays.map((wd) => (
              <div className={styles.dayColumn} key={wd}>
                <div className={styles.dayHeader}>
                  {wd.charAt(0).toUpperCase() + wd.slice(1)}
                </div>
                <div className={styles.dayBody}>
                  {axes.map((ax) => (
                    <div className={styles.axisBlock} key={ax}>
                      <div className={styles.axisTitle}>{ax.toUpperCase()}</div>
                      <ul className={styles.activityList}>
                        {(getDayList(ax, wd) || []).map((a, idx) => (
                          <li key={a.id || idx} className={styles.activityItem}>
                            <div>
                              <div className={styles.actTitle}>{a.title}</div>
                              <div className={styles.actMeta}>
                                {a.userCreated ? "Personalizada" : "Sugestão"}
                              </div>
                            </div>
                            <div>
                              <button
                                onClick={() => removeFromDay(ax, wd, idx)}
                                className={styles.removeBtn}
                              >
                                Remover
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
